<script setup lang="ts">
import { startCase } from "lodash-es"
import moment from "moment"

import type { Order } from "@Modules/shoppingcart/Data/Models/OrderModel"
import { currencyFormat } from "@Helpers/support"
import Spinner from "@Components/Spinner.vue"

//
// Props
interface Props {
  orders: Order[]
  loading: boolean
  orgSlug: string | null
  locale: string
  translatedTransactionQuantity: string
  recoverNow: (order: { id: number }) => Promise<void>
}

const props = defineProps<Props>()

//
// Methods
function getUpdatedDate(order: { updated_at: string }) {
  return moment.utc(order.updated_at)
}

function formatOrderAmount(order: Order): string {
  const amount = order.amount()
  if (!amount || amount.amount === null || amount.currency === null) return "--"
  return currencyFormat(amount.amount, amount.currency, props.locale)
}
</script>

<template>
  <div
    data-test-id="org_orders_view"
    class="container-fluid my-5"
  >
    <h5 class="text-muted">
      Orders Management
    </h5>
    <h2>Orders History</h2>
    <br>

    <div class="row">
      <div class="col-12">
        <div class="card card-body p-0 mb-3 shadow-none overflow-hidden">
          <div class="table-responsive">
            <table class="table table-striped table-valign-middle mb-0">
              <thead>
                <tr class="text-white bg-blue-dark">
                  <th class="border-top-0 font-weight-semibold">
                    Description
                  </th>
                  <th class="border-top-0 font-weight-semibold">
                    Date
                  </th>
                  <th class="border-top-0 font-weight-semibold">
                    Payment
                  </th>
                  <th class="border-top-0 font-weight-semibold">
                    Total
                  </th>
                  <th class="border-top-0 font-weight-semibold th-autofit" />
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td
                    class="py-4"
                    colspan="5"
                  >
                    <Spinner />
                  </td>
                </tr>
                <template v-else>
                  <template v-if="orders?.length">
                    <tr
                      v-for="order in orders"
                      :key="order.id"
                      class="_row"
                    >
                      <td class="td-fit">
                        <div
                          v-for="item in order.items"
                          :key="`iK-${item.id}`"
                          :title="item.title"
                        >
                          {{ item.title }}
                        </div>
                      </td>
                      <td class="td-fit _order_date">
                        {{ getUpdatedDate(order).format("MMMM DD, YYYY") }}
                      </td>
                      <td class="td-fit">
                        <div class="flex-center">
                          <div class="mr-3">
                            <template v-if="order.isNew()">
                              &nbsp;
                            </template>
                            <template v-else>
                              <p class="mb-0">
                                Credit Card
                              </p>
                              <p
                                class="mb-0 text-xs"
                                :class="order.paymentStatusColor()"
                              >
                                {{ startCase(order.paymentStatusLabel()) }}
                              </p>
                            </template>
                          </div>
                          <button
                            v-if="order.canRecover()"
                            type="button"
                            class="btn btn-blue btn-sm _retry"
                            @click="recoverNow(order)"
                          >
                            Retry
                          </button>
                        </div>
                      </td>
                      <td class="td-fit">
                        {{ formatOrderAmount(order) }}
                      </td>
                      <td class="td-fit text-center">
                        <router-link
                          :to="{
                            name: 'org.shopping-cart.orders.order',
                            params: { org: orgSlug, orderId: order.id },
                          }"
                          class="btn btn-outline-blue _view_order"
                        >
                          View Order
                        </router-link>
                      </td>
                    </tr>
                  </template>
                  <tr v-else>
                    <td
                      class="text-center _empty"
                      colspan="5"
                    >
                      <i class="fa-solid fa-circle-exclamation" />
                      No order(s) found.
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div
      v-show="!loading"
      class="_count"
    >
      {{ translatedTransactionQuantity }}
    </div>
  </div>
</template>
